import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Layout } from '../components/Layout';
import {
  CheckCircle, Clock, AlertCircle, ExternalLink, 
  Building, Phone, MapPin, Globe, TrendingUp,
  Zap, Settings, RefreshCw, Send, ChevronRight,
  Database, Network, ArrowRight, Info, Rocket,
  ShieldCheck, Search, Eye, Link
} from 'lucide-react';

export function CitationDashboard() {
  const [directories, setDirectories] = useState([]);
  const [citations, setCitations] = useState([]);
  const [nap, setNap] = useState(null);
  const [consistency, setConsistency] = useState(null);
  const [aggregatorInfo, setAggregatorInfo] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [syndicationStatus, setSyndicationStatus] = useState(null);
  const [manualGuide, setManualGuide] = useState(null);
  const [verificationChecklist, setVerificationChecklist] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [editingUrl, setEditingUrl] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      
      const [
        dirResponse, 
        citResponse, 
        napResponse, 
        consResponse,
        aggInfoResponse,
        submissionsResponse,
        syndicationResponse,
        guideResponse,
        verificationResponse
      ] = await Promise.all([
        api.request('/seo/citations/directories').catch(() => ({ directories: [], total: 0 })),
        api.request('/seo/citations/citations').catch(() => ({ citations: [], total: 0 })),
        api.request('/seo/citations/nap').catch(() => null),
        api.request('/seo/citations/consistency-check').catch(() => null),
        api.request('/seo/aggregators/info').catch(() => null),
        api.request('/seo/aggregators/submissions').catch(() => ({ submissions: [], total: 0 })),
        api.request('/seo/aggregators/syndication-status').catch(() => null),
        api.request('/seo/aggregators/manual-submission-guide').catch(() => null),
        api.request('/seo/aggregators/verification-checklist').catch(() => null)
      ]);
      
      setDirectories(dirResponse?.directories || []);
      setCitations(citResponse?.citations || []);
      setNap(napResponse);
      setConsistency(consResponse);
      setAggregatorInfo(aggInfoResponse);
      setSubmissions(submissionsResponse?.submissions || []);
      setSyndicationStatus(syndicationResponse);
      setManualGuide(guideResponse);
      setVerificationChecklist(verificationResponse);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSubmit = async () => {
    try {
      setSubmitting(true);
      const result = await api.request('/seo/aggregators/auto-submit-all', {
        method: 'POST'
      });
      
      alert(result.success 
        ? `Successfully submitted to ${result.submitted_to?.join(', ')}! Syndicating to ${result.result?.total_syndication_reach || 0}+ directories.`
        : result.message || 'Submission failed'
      );
      
      // Reload data
      loadAllData();
    } catch (error) {
      console.error('Auto-submit error:', error);
      alert('Failed to auto-submit. Check console for details.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyAll = async () => {
    try {
      setVerifying(true);
      const result = await api.request('/seo/aggregators/verify-all');
      
      alert(`Verification complete! ${result.summary?.live || 0} live, ${result.summary?.pending || 0} pending verification.`);
      
      // Reload data
      loadAllData();
    } catch (error) {
      console.error('Verification error:', error);
      alert('Verification check failed. See console for details.');
    } finally {
      setVerifying(false);
    }
  };

  const handleMarkVerified = async (submissionId, listingUrl) => {
    try {
      const url = listingUrl || urlInput;
      if (!url) {
        alert('Please provide the listing URL');
        return;
      }
      
      await api.request(`/seo/aggregators/verify-submission/${submissionId}?listing_url=${encodeURIComponent(url)}&status=live`, {
        method: 'POST'
      });
      
      setEditingUrl(null);
      setUrlInput('');
      alert('Listing marked as verified!');
      loadAllData();
    } catch (error) {
      console.error('Mark verified error:', error);
      alert('Failed to mark as verified');
    }
  };

  const handleSubmitToAggregator = async (aggregator) => {
    try {
      setSubmitting(true);
      const result = await api.request('/seo/aggregators/submit', {
        method: 'POST',
        body: JSON.stringify({ aggregators: [aggregator] })
      });
      
      const aggResult = result.results?.find(r => r.aggregator === aggregator);
      if (aggResult?.success) {
        alert(`Successfully submitted to ${aggregator}! Venue ID: ${aggResult.venue_id}`);
      } else {
        alert(`Submission to ${aggregator}: ${aggResult?.message || aggResult?.error || 'Failed'}`);
      }
      
      loadAllData();
    } catch (error) {
      console.error('Submit error:', error);
      alert('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'submitted':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'rejected':
      case 'inconsistent':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const filteredDirectories = selectedPriority === 'all' 
    ? directories 
    : directories.filter(d => d.priority === parseInt(selectedPriority));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-lg">Loading citation data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Citation & Directory Manager</h1>
            <p className="text-gray-600 mt-1">Automated business listing syndication across 100+ directories</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadAllData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleAutoSubmit} disabled={submitting} className="bg-gradient-to-r from-blue-600 to-purple-600">
              <Rocket className="w-4 h-4 mr-2" />
              {submitting ? 'Submitting...' : 'Auto-Submit All'}
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Reach</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {syndicationStatus?.total_directory_coverage || 0}+
                  </p>
                </div>
                <Network className="w-10 h-10 text-blue-200" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Directories covered</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">API Submissions</p>
                  <p className="text-2xl font-bold text-green-600">
                    {syndicationStatus?.api_submissions?.verified || 0}
                  </p>
                </div>
                <Zap className="w-10 h-10 text-green-200" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Auto-syndicated</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Manual Citations</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {syndicationStatus?.manual_citations?.verified || 0}
                  </p>
                </div>
                <Building className="w-10 h-10 text-purple-200" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Manually submitted</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">NAP Consistency</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {consistency?.consistency_score || 100}%
                  </p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-200" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Across all listings</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="verification">
              <ShieldCheck className="w-4 h-4 mr-1" />
              Verify
            </TabsTrigger>
            <TabsTrigger value="aggregators">Aggregators</TabsTrigger>
            <TabsTrigger value="directories">Directories</TabsTrigger>
            <TabsTrigger value="guide">Setup Guide</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {/* Master NAP */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Master NAP Information
                </CardTitle>
                <CardDescription>Your canonical business information used across all directories</CardDescription>
              </CardHeader>
              <CardContent>
                {nap ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{nap.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span>{nap.address}, {nap.city}, {nap.state} {nap.zip_code}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span>{nap.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <a href={nap.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {nap.website}
                        </a>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{nap.description}</p>
                      {nap.contact_name && (
                        <p className="text-sm text-gray-500 mt-2">
                          Contact: {nap.contact_name} ({nap.contact_title})
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">No NAP information configured</p>
                )}
              </CardContent>
            </Card>

            {/* Recommendations */}
            {syndicationStatus?.recommendations && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {syndicationStatus.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 mt-1 text-blue-500" />
                        <span className="text-sm">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Recent Submissions */}
            <Card>
              <CardHeader>
                <CardTitle>Recent API Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                {submissions.length > 0 ? (
                  <div className="space-y-3">
                    {submissions.slice(0, 5).map((sub, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(sub.status)}
                          <div>
                            <p className="font-medium">{sub.business_name}</p>
                            <p className="text-sm text-gray-500">
                              {sub.aggregator} • Reach: {sub.syndication_reach}+ directories
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={sub.status === 'verified' ? 'default' : 'secondary'}>
                            {sub.status}
                          </Badge>
                          {sub.external_id && (
                            <p className="text-xs text-gray-400 mt-1">ID: {sub.external_id}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No API submissions yet</p>
                    <Button onClick={handleAutoSubmit} className="mt-4" disabled={submitting}>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verification Tab */}
          <TabsContent value="verification" className="space-y-4">
            {/* Verification Header */}
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-green-600" />
                      Listing Verification Center
                    </CardTitle>
                    <CardDescription>
                      Check if your directory submissions are live and publicly visible
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={handleVerifyAll} 
                    disabled={verifying}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${verifying ? 'animate-spin' : ''}`} />
                    {verifying ? 'Checking...' : 'Check All Listings'}
                  </Button>
                </div>
              </CardHeader>
              {verificationChecklist?.stats && (
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{verificationChecklist.stats.verified}</p>
                      <p className="text-xs text-gray-500">Verified Live</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-yellow-600">{verificationChecklist.stats.pending_verification}</p>
                      <p className="text-xs text-gray-500">Pending Verification</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600">{verificationChecklist.stats.submitted}</p>
                      <p className="text-xs text-gray-500">Total Submitted</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-gray-600">{verificationChecklist.stats.not_submitted}</p>
                      <p className="text-xs text-gray-500">Not Yet Submitted</p>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Verification Checklist */}
            <Card>
              <CardHeader>
                <CardTitle>Directory Verification Checklist</CardTitle>
                <CardDescription>
                  Verify each submission is live by checking the directory and providing the listing URL
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {verificationChecklist?.checklist?.map((item, i) => (
                    <div 
                      key={i} 
                      className={`border rounded-lg p-4 ${
                        item.verification_status === 'live' ? 'bg-green-50 border-green-200' :
                        item.submission_status === 'submitted' ? 'bg-yellow-50 border-yellow-200' :
                        'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {item.verification_status === 'live' ? (
                            <CheckCircle className="w-6 h-6 text-green-500" />
                          ) : item.submission_status === 'submitted' ? (
                            <Clock className="w-6 h-6 text-yellow-500" />
                          ) : (
                            <AlertCircle className="w-6 h-6 text-gray-400" />
                          )}
                          <div>
                            <h3 className="font-semibold">{item.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={item.importance === 'CRITICAL' ? 'destructive' : 'secondary'}>
                                {item.importance}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {item.syndication_reach}+ directories
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {item.verification_method === 'api' ? '🤖 Auto-verify' : '👤 Manual'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {item.dashboard_url && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={item.dashboard_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4 mr-1" />
                                Dashboard
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Verification Status */}
                      <div className="bg-white rounded-lg p-3 mt-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              Status: {' '}
                              <span className={
                                item.verification_status === 'live' ? 'text-green-600' :
                                item.submission_status === 'submitted' ? 'text-yellow-600' :
                                'text-gray-500'
                              }>
                                {item.verification_status === 'live' ? '✅ Verified Live' :
                                 item.submission_status === 'submitted' ? '⏳ Submitted - Needs Verification' :
                                 '❌ Not Submitted'}
                              </span>
                            </p>
                            {item.listing_url && (
                              <a 
                                href={item.listing_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                              >
                                <Link className="w-3 h-3" />
                                {item.listing_url}
                              </a>
                            )}
                            {item.last_checked && (
                              <p className="text-xs text-gray-400 mt-1">
                                Last checked: {new Date(item.last_checked).toLocaleString()}
                              </p>
                            )}
                          </div>
                          
                          {/* Verification Actions */}
                          {item.submission_id && item.verification_status !== 'live' && (
                            <div className="flex items-center gap-2">
                              {editingUrl === item.submission_id ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    placeholder="Paste listing URL..."
                                    value={urlInput}
                                    onChange={(e) => setUrlInput(e.target.value)}
                                    className="w-64"
                                  />
                                  <Button 
                                    size="sm" 
                                    onClick={() => handleMarkVerified(item.submission_id)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Confirm
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingUrl(null);
                                      setUrlInput('');
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => setEditingUrl(item.submission_id)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  I&apos;ve Verified This
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Verification Tips */}
                      {item.tips && item.verification_status !== 'live' && (
                        <div className="mt-3 bg-blue-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-blue-800 mb-2">
                            <Search className="w-3 h-3 inline mr-1" />
                            How to verify:
                          </p>
                          <ul className="text-xs text-blue-700 space-y-1">
                            {item.tips.map((tip, j) => (
                              <li key={j}>• {tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}

                  {(!verificationChecklist?.checklist || verificationChecklist.checklist.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No submissions to verify yet</p>
                      <p className="text-sm">Submit to directories first, then come back to verify</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aggregators Tab */}
          <TabsContent value="aggregators" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Data Aggregators</CardTitle>
                <CardDescription>
                  Aggregators syndicate your listing to multiple directories automatically
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {aggregatorInfo?.aggregators?.map((agg, i) => (
                    <div key={i} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            agg.is_configured ? 'bg-green-500' : 
                            agg.status === 'manual_only' ? 'bg-yellow-500' : 'bg-gray-300'
                          }`} />
                          <div>
                            <h3 className="font-semibold">{agg.name}</h3>
                            <p className="text-sm text-gray-500">{agg.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="mb-1">
                            {agg.syndication_reach}+ directories
                          </Badge>
                          <p className="text-xs text-gray-400">
                            {agg.free_tier ? 'Free tier available' : agg.pricing || 'Paid only'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mt-3 pt-3 border-t">
                        <div className="flex items-center gap-2">
                          {agg.api_available ? (
                            <Badge variant="secondary">API Available</Badge>
                          ) : (
                            <Badge variant="outline">Manual Only</Badge>
                          )}
                          {agg.is_configured && (
                            <Badge className="bg-green-100 text-green-800">Configured</Badge>
                          )}
                        </div>
                        
                        {agg.api_available && agg.is_configured ? (
                          <Button 
                            size="sm" 
                            onClick={() => handleSubmitToAggregator(agg.id)}
                            disabled={submitting}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Submit
                          </Button>
                        ) : agg.manual_url ? (
                          <Button size="sm" variant="outline" asChild>
                            <a href={agg.manual_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Manual Setup
                            </a>
                          </Button>
                        ) : agg.api_available && !agg.is_configured ? (
                          <Button size="sm" variant="outline" disabled>
                            <Settings className="w-4 h-4 mr-2" />
                            Needs API Key
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Directories Tab */}
          <TabsContent value="directories" className="space-y-4">
            {/* Priority Filter */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedPriority === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedPriority('all')}
              >
                All ({directories.length})
              </Button>
              {[1, 2, 3].map(p => (
                <Button
                  key={p}
                  variant={selectedPriority === String(p) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPriority(String(p))}
                >
                  Priority {p} ({directories.filter(d => d.priority === p).length})
                </Button>
              ))}
            </div>

            {/* Directory List */}
            <div className="grid gap-3">
              {filteredDirectories.map((dir, i) => {
                const citation = citations.find(c => c.directory_name === dir.name);
                return (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(citation?.status)}
                          <div>
                            <h3 className="font-medium">{dir.name}</h3>
                            <p className="text-sm text-gray-500">Priority {dir.priority} • {dir.category}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={citation?.status === 'verified' ? 'default' : 'outline'}>
                            {citation?.status || 'Not Started'}
                          </Badge>
                          <Button size="sm" variant="ghost" asChild>
                            <a href={dir.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Guide Tab */}
          <TabsContent value="guide" className="space-y-4">
            {manualGuide && (
              <>
                {/* Priority 1 */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-red-600">🔴 Priority 1 - Critical</CardTitle>
                    <CardDescription>Do these first - highest SEO impact</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {manualGuide.priority_1_major?.map((item, i) => (
                        <div key={i} className="border rounded-lg p-4 bg-white">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="font-bold text-lg">{item.name}</h3>
                              <p className="text-sm text-red-600 font-medium">{item.importance}</p>
                              {item.syndication && (
                                <p className="text-xs text-gray-500">Syndicates to: {item.syndication}</p>
                              )}
                            </div>
                            <Button size="sm" asChild>
                              <a href={item.url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Open
                              </a>
                            </Button>
                          </div>
                          
                          {/* Main Steps */}
                          {item.steps && (
                            <div className="mb-4">
                              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">STEPS</span>
                                Step-by-Step Instructions
                              </h4>
                              <ol className="space-y-2 text-sm">
                                {item.steps.map((step, j) => (
                                  <li key={j} className="flex items-start gap-2">
                                    <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                                      {j + 1}
                                    </span>
                                    <span className={step.includes('IMPORTANT') ? 'text-red-600 font-medium' : ''}>
                                      {step}
                                    </span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}
                          
                          {/* Online Business Tips */}
                          {item.online_business_tips && (
                            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-amber-800">
                                <Info className="w-4 h-4" />
                                Tips for Online/SaaS Businesses
                              </h4>
                              <ul className="space-y-1 text-sm text-amber-900">
                                {item.online_business_tips.map((tip, j) => (
                                  <li key={j} className="flex items-start gap-2">
                                    <span className="text-amber-500">•</span>
                                    {tip}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Verification Options */}
                          {item.verification_options && (
                            <div className="mb-4">
                              <h4 className="font-semibold text-sm mb-2">Verification Methods</h4>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {Object.entries(item.verification_options).map(([method, desc]) => (
                                  <div key={method} className="bg-gray-50 rounded p-2 text-center">
                                    <p className="font-medium text-xs capitalize">{method.replace('_', ' ')}</p>
                                    <p className="text-xs text-gray-500">{desc}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Common Mistakes */}
                          {item.common_mistakes && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-red-800">
                                <AlertCircle className="w-4 h-4" />
                                Common Mistakes to Avoid
                              </h4>
                              <ul className="space-y-1 text-sm text-red-900">
                                {item.common_mistakes.map((mistake, j) => (
                                  <li key={j} className="flex items-start gap-2">
                                    <span className="text-red-500">✗</span>
                                    {mistake}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {item.time_estimate && (
                            <p className="text-xs text-gray-400 mt-3">⏱️ {item.time_estimate}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Priority 2 */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-orange-600">🟠 Priority 2 - Important</CardTitle>
                    <CardDescription>Do these after completing Priority 1</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-3">
                      {manualGuide.priority_2_important?.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <h3 className="font-medium">{item.name}</h3>
                            <p className="text-xs text-gray-500">{item.importance}</p>
                          </div>
                          <Button size="sm" variant="ghost" asChild>
                            <a href={item.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Tips */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Info className="w-5 h-5" />
                      Pro Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {manualGuide.tips?.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 mt-1 text-green-500 flex-shrink-0" />
                          <span className="text-sm">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
    </Layout>
  );
}

export default CitationDashboard;
